/**
 * Simple entry-type forms. Each receives `value` (payload dict) and calls
 * `onChange(nextPayload)`. Parent (EntryDialog) captures the shared
 * Title + Detail so these forms cover only type-specific extra fields.
 */
import { PAPER, INPUT_CLASS } from '../theme';

// ---------- Delivery ----------
export const DeliveryForm = ({ value, onChange }) => {
  const v = value || {};
  const set = (k) => (e) => onChange({ ...v, [k]: e.target.value });
  return (
    <div className="grid gap-3">
      <Field label="Supplier">
        <input type="text" value={v.supplier || ''} onChange={set('supplier')}
          placeholder="e.g. Travis Perkins" className={INPUT_CLASS} />
      </Field>
      <Field label="Materials / description">
        <input type="text" value={v.materials || ''} onChange={set('materials')}
          placeholder="e.g. 20 × 6m 4x2 C24" className={INPUT_CLASS} />
      </Field>
      <Field label="Quantity">
        <input type="text" value={v.quantity || ''} onChange={set('quantity')}
          placeholder="e.g. 2 pallets" className={INPUT_CLASS} />
      </Field>
    </div>
  );
};

// ---------- Weather ----------
const WEATHER_CONDITIONS = ['Dry', 'Overcast', 'Light rain', 'Heavy rain', 'Snow', 'Frost', 'Strong wind', 'Very hot'];
export const WeatherForm = ({ value, onChange }) => {
  const v = value || {};
  return (
    <div className="grid gap-3">
      <Field label="Conditions">
        <div className="flex flex-wrap gap-1.5">
          {WEATHER_CONDITIONS.map(c => {
            const active = v.condition === c;
            return (
              <button
                key={c}
                type="button"
                onClick={() => onChange({ ...v, condition: c })}
                className="text-[12px] px-3 py-1.5 rounded-full font-medium transition-colors"
                style={{
                  background: active ? '#CFFAFE' : PAPER.card,
                  border: `1px solid ${active ? '#67E8F9' : PAPER.border}`,
                  color: active ? '#0E7490' : PAPER.inkSoft,
                }}
              >{c}</button>
            );
          })}
        </div>
      </Field>
      <Field label="Was work affected?">
        <div className="flex gap-2">
          {[['no', 'No'], ['partial', 'Partly'], ['stopped', 'Stopped']].map(([k, label]) => {
            const active = v.impact === k;
            return (
              <button
                key={k}
                type="button"
                onClick={() => onChange({ ...v, impact: k })}
                className="flex-1 text-[13px] font-medium py-2 rounded-md transition-colors"
                style={{
                  background: active ? '#FEF3C7' : PAPER.card,
                  border: `1px solid ${active ? '#FCD34D' : PAPER.border}`,
                  color: active ? '#92400E' : PAPER.inkSoft,
                }}
              >{label}</button>
            );
          })}
        </div>
      </Field>
      {v.impact && v.impact !== 'no' && (
        <Field label="How long?">
          <input
            type="text" value={v.duration || ''} onChange={(e) => onChange({ ...v, duration: e.target.value })}
            placeholder="e.g. all morning · 2 hrs"
            className={INPUT_CLASS}
          />
        </Field>
      )}
    </div>
  );
};

// ---------- Delay / Issue ----------
export const DelayForm = ({ value, onChange }) => {
  const v = value || {};
  const set = (k) => (e) => onChange({ ...v, [k]: e.target.value });
  return (
    <div className="grid gap-3">
      <Field label="Cause">
        <input type="text" value={v.cause || ''} onChange={set('cause')}
          placeholder="e.g. Missing materials · Access blocked" className={INPUT_CLASS} />
      </Field>
      <Field label="Impact">
        <div className="flex gap-2">
          {['minor', 'half day', 'full day', '>1 day'].map(k => {
            const active = v.impact === k;
            return (
              <button
                key={k}
                type="button"
                onClick={() => onChange({ ...v, impact: k })}
                className="flex-1 text-[13px] font-medium py-2 rounded-md transition-colors"
                style={{
                  background: active ? '#FEE2E2' : PAPER.card,
                  border: `1px solid ${active ? '#FCA5A5' : PAPER.border}`,
                  color: active ? '#B91C1C' : PAPER.inkSoft,
                }}
              >{k}</button>
            );
          })}
        </div>
      </Field>
    </div>
  );
};

// ---------- RFI ----------
export const RfiForm = ({ value, onChange }) => {
  const v = value || {};
  return (
    <div className="grid gap-3">
      <div
        className="text-[12px] rounded-md px-3 py-2.5 leading-snug"
        style={{ background: PAPER.card, border: `1px solid ${PAPER.border}`, color: PAPER.inkSoft }}
      >
        A reference (RFI-###) is generated automatically on save. Full follow-up happens later in the office — this is just the on-site capture.
      </div>
      <Field label="Who / what is this for?">
        <input
          type="text" value={v.raised_with || ''}
          onChange={(e) => onChange({ ...v, raised_with: e.target.value })}
          placeholder="e.g. Architect · Client · Structural engineer"
          className={INPUT_CLASS}
        />
      </Field>
    </div>
  );
};

// ---------- Instruction ----------
export const InstructionForm = ({ value, onChange }) => {
  const v = value || {};
  return (
    <div className="grid gap-3">
      <div
        className="text-[12px] rounded-md px-3 py-2.5 leading-snug"
        style={{ background: PAPER.card, border: `1px solid ${PAPER.border}`, color: PAPER.inkSoft }}
      >
        A reference (INS-###) is generated automatically on save.
      </div>
      <Field label="Direction">
        <div className="flex gap-2">
          {[['received', 'Received'], ['issued', 'Issued']].map(([k, l]) => {
            const active = v.direction === k;
            return (
              <button
                key={k}
                type="button"
                onClick={() => onChange({ ...v, direction: k })}
                className="flex-1 text-[13px] font-medium py-2 rounded-md transition-colors"
                style={{
                  background: active ? '#EDE9FE' : PAPER.card,
                  border: `1px solid ${active ? '#C4B5FD' : PAPER.border}`,
                  color: active ? '#6D28D9' : PAPER.inkSoft,
                }}
              >{l}</button>
            );
          })}
        </div>
      </Field>
      <Field label="From / to">
        <input
          type="text" value={v.party || ''}
          onChange={(e) => onChange({ ...v, party: e.target.value })}
          placeholder="Name of the other party"
          className={INPUT_CLASS}
        />
      </Field>
    </div>
  );
};

// ---------- Site Visit / Meeting ----------
export const VisitForm = ({ value, onChange }) => {
  const v = value || {};
  return (
    <div className="grid gap-3">
      <Field label="Who visited / attended">
        <input
          type="text" value={v.attendees || ''}
          onChange={(e) => onChange({ ...v, attendees: e.target.value })}
          placeholder="e.g. Client + Architect · Building inspector"
          className={INPUT_CLASS}
        />
      </Field>
      <Field label="What was agreed / discussed">
        <textarea
          value={v.notes || ''}
          onChange={(e) => onChange({ ...v, notes: e.target.value })}
          rows={4}
          placeholder="Short summary — actions, decisions, follow-ups"
          className={`${INPUT_CLASS} font-mono`}
        />
      </Field>
    </div>
  );
};

// ---------- Shared bits ----------
const Field = ({ label, children }) => (
  <div>
    <label className="block text-[11px] uppercase tracking-wider font-semibold mb-1.5" style={{ color: PAPER.muted }}>
      {label}
    </label>
    {children}
  </div>
);
